import * as React from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-basic-dist-min';
import './CollaborationNetworkGraph.css';

const Plot = createPlotlyComponent(Plotly);

// Configurable thresholds for easy future adjustments (in Euros)
const FUNDING_THRESHOLDS = {
  MEDIUM: 500000,   // 0.5 M€
  LARGE: 1000000,   // 1.0 M€
};

// Configurable marker sizes
const MARKER_SIZES = {
  CENTER: 38,
  CENTER_HALO: 56,
  LARGE: 24,
  MEDIUM: 17,
  SMALL: 11,
};

// Spinbase color scheme constants
const COLORS = {
  PRIMARY_RED: '#ed1c24',
  DARK_RED: '#b3151a',
  LIGHT_RED_BG: '#ffffff', // Crisp white glassmorphic fill
  WHITE: '#ffffff',
  BORDER_GREY: '#B7B8B9',
  TEXT_DARK: '#222222',
  TEXT_MUTED: '#707070',
  LINE_DEFAULT: 'rgba(237, 28, 36, 0.45)', // Increased opacity for better visibility
  LINE_HIGHLIGHT: 'rgba(237, 28, 36, 0.95)',
  LINE_FADED: 'rgba(180, 180, 180, 0.05)',  // Very faint lines for other nodes
  NODE_FADED: 'rgba(200, 200, 200, 0.3)',   // Faded nodes on hover
};

interface ICollaboration {
  pic: string;
  legalName: string;
  businessName?: string;
  classificationType?: string;
  numberOfProjects: number;
  jointFunding?: number;
}

interface ICollaborationNetworkGraphProps {
  collaborations: ICollaboration[];
  centerPartner: {
    id: number | string;
    name: string;
    activityType?: string;
  };
}

export const CollaborationNetworkGraph: React.FC<ICollaborationNetworkGraphProps> = ({
  collaborations = [],
  centerPartner,
}) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  // 1. Filter out partners with < 2 joint projects, sort, and limit to top 30
  const sortedCollaborations = React.useMemo(() => {
    return collaborations
      .filter((collab) => (collab.numberOfProjects || 0) >= 2)
      .sort((a, b) => (b.numberOfProjects || 0) - (a.numberOfProjects || 0))
      .slice(0, 30); // Limit to top 30 main collaborations
  }, [collaborations]);

  // Compute maximum radius for layout bounds to prevent zooming out into a tiny dot
  const maxRadius = React.useMemo(() => {
    return Math.max(12, sortedCollaborations.length * 1.5);
  }, [sortedCollaborations.length]);

  // Dynamically scale graph height based on network size
  const graphHeight = React.useMemo(() => {
    const N = sortedCollaborations.length;
    if (N <= 5) return '400px';
    if (N <= 20) return '550px';
    if (N <= 50) return '750px';
    if (N <= 100) return '900px';
    return '1100px'; // For massive networks!
  }, [sortedCollaborations.length]);

  // 2. Generate 100% deterministic organic force-directed coordinates
  const nodeCoordinates = React.useMemo(() => {
    const N = sortedCollaborations.length;
    if (N === 0) return [];

    // Scale the spread area based on the number of nodes
    const minRadius = 6.0;

    // Initialize node positions deterministically using sin/cos of the index to prevent overlap
    const nodes = sortedCollaborations.map((collab, i) => {
      // Scatter initial positions in concentric rings based on index
      const angle = (i * 2.39996) + 0.5; // Golden angle multiplier for natural spiral scattering
      const baseRadius = minRadius + (i / N) * maxRadius; // Slowly expand radius outward
      return {
        x: Math.cos(angle) * baseRadius,
        y: Math.sin(angle) * baseRadius,
      };
    });

    // Run simple spring-embedder force simulation iterations (runs in <1ms)
    const iterations = 80;
    const attractionConstant = 0.05; // Pull strength toward target concentric orbits

    for (let iter = 0; iter < iterations; iter++) {
      // A. Repulsion between all partner nodes to prevent overcrowding/overlaps
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distSqr = dx * dx + dy * dy || 0.001;
          const dist = Math.sqrt(distSqr);

          // Force-directed repulsion logic
          const minDist = 6.0; // Desired minimum distance between nodes (increased to fit text)
          if (dist < minDist) {
            const force = ((minDist - dist) / dist) * 0.45;
            const fx = dx * force;
            const fy = dy * force;
            nodes[i].x += fx;
            nodes[i].y += fy;
            nodes[j].x -= fx;
            nodes[j].y -= fy;
          }
        }
      }

      // B. Gentle attraction to their designated circular orbits around center (0,0)
      for (let i = 0; i < N; i++) {
        const x = nodes[i].x;
        const y = nodes[i].y;
        const dist = Math.sqrt(x * x + y * y) || 0.001;
        const targetRadius = minRadius + (i / N) * maxRadius;
        const force = (targetRadius - dist) * attractionConstant;

        nodes[i].x += (x / dist) * force;
        nodes[i].y += (y / dist) * force;
      }
    }

    return nodes;
  }, [sortedCollaborations]);

  // 3. Build Plotly traces and static text box annotations
  const graphData = React.useMemo(() => {
    const N = sortedCollaborations.length;
    if (N === 0) return { traces: [], annotations: [] };

    const c_max = Math.max(...sortedCollaborations.map(c => c.numberOfProjects || 0), 1);
    const c_min = Math.min(...sortedCollaborations.map(c => c.numberOfProjects || 0), c_max);
    const range = Math.max(c_max - c_min, 1); // Prevent division by zero

    const traces: any[] = [];
    const annotations: any[] = [];

    // -------------------------------------------------------------
    // STEP A: Build Traces for connection lines (edges)
    // -------------------------------------------------------------
    sortedCollaborations.forEach((collab, index) => {
      const coord = nodeCoordinates[index];
      if (!coord) return;

      // Edge thickness based on normalized number of collaborations within the top group
      const projects = collab.numberOfProjects || 0;
      const normalizedRatio = (projects - c_min) / range;
      
      let strokeWidth = 2.5; // Thin
      if (normalizedRatio > 0.66) {
        strokeWidth = 9.0; // Thickest
      } else if (normalizedRatio >= 0.33) {
        strokeWidth = 5.5; // Medium thickness
      }
      
      // Calculate hover focus highlight state
      const isCurrentHovered = hoveredIndex === (index + 1);
      let lineColor = COLORS.LINE_DEFAULT;
      let lineWidth = strokeWidth;

      if (hoveredIndex !== null) {
        if (isCurrentHovered) {
          lineColor = COLORS.LINE_HIGHLIGHT;
          lineWidth = strokeWidth + 2.0; // Highlighted line
        } else {
          lineColor = COLORS.LINE_FADED; // Dimmed line
        }
      }

      traces.push({
        x: [0, coord.x],
        y: [0, coord.y],
        type: 'scatter',
        mode: 'lines',
        line: {
          color: lineColor,
          width: lineWidth,
        },
        hoverinfo: 'skip',
        showlegend: false,
      });
    });

    // -------------------------------------------------------------
    // STEP B: Build Trace for Center Node Glowing Halo
    // -------------------------------------------------------------
    traces.push({
      x: [0],
      y: [0],
      type: 'scatter',
      mode: 'markers',
      marker: {
        size: [MARKER_SIZES.CENTER_HALO],
        color: ['rgba(237, 28, 36, 0.15)'],
        line: {
          color: 'rgba(237, 28, 36, 0.05)',
          width: 0,
        },
      },
      hoverinfo: 'skip',
      showlegend: false,
    });

    // -------------------------------------------------------------
    // STEP C: Build Trace for all Actor Nodes (Center + Partners)
    // -------------------------------------------------------------
    // Calculate totals for center node
    let totalJointFunding = 0;
    let totalJointProjects = 0;
    sortedCollaborations.forEach(c => {
      totalJointFunding += (c.jointFunding || 0);
      totalJointProjects += (c.numberOfProjects || 0);
    });
    
    const formattedCenterFunding = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(totalJointFunding);

    const xCoordinates: number[] = [0];
    const yCoordinates: number[] = [0];
    const sizes: number[] = [MARKER_SIZES.CENTER];
    
    const colors: string[] = [COLORS.PRIMARY_RED];
    const outlineColors: string[] = [COLORS.DARK_RED];
    const outlineWidths: number[] = [2.5];
    const hoverTexts: string[] = [
      `<b>${centerPartner.name}</b><br><i>(Active Organization)</i><br>Joint Funding: ${formattedCenterFunding}<br>Joint Projects: ${totalJointProjects}`
    ];
    const customData: any[] = [{ type: 'center', pic: centerPartner.id, index: 0 }];

    if (hoveredIndex !== 0) {
      annotations.push({
        x: 0,
        y: 0,
        text: `<b>${centerPartner.name}</b>`,
        showarrow: false,
        yshift: -30,
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        bordercolor: COLORS.PRIMARY_RED,
        borderpad: 5,
        font: { size: 10, color: COLORS.TEXT_DARK }
      });
    }

    // Populate outer partner nodes coordinates and properties
    sortedCollaborations.forEach((collab, index) => {
      const partnerIndex = index + 1;
      const coord = nodeCoordinates[index];
      if (!coord) return;

      xCoordinates.push(coord.x);
      yCoordinates.push(coord.y);

      // Quantize funding amount into 3 bucket sizes
      const funding = collab.jointFunding || 0;
      let size = MARKER_SIZES.SMALL;

      if (funding >= FUNDING_THRESHOLDS.LARGE) {
        size = MARKER_SIZES.LARGE;
      } else if (funding >= FUNDING_THRESHOLDS.MEDIUM) {
        size = MARKER_SIZES.MEDIUM;
      }

      // If another node is hovered, dim this one out to focus on the hovered path
      const isAnyHovered = hoveredIndex !== null;
      const isThisHovered = hoveredIndex === partnerIndex;

      let nodeColor = COLORS.LIGHT_RED_BG;
      let outlineColor = COLORS.PRIMARY_RED;
      let outlineWidth = 1.5;

      if (isAnyHovered) {
        if (isThisHovered) {
          size += 3; // Pop up hovered node
          outlineColor = COLORS.DARK_RED;
          outlineWidth = 2.5;
        } else {
          nodeColor = COLORS.NODE_FADED;
          outlineColor = 'rgba(237, 28, 36, 0.15)';
        }
      }

      sizes.push(size);
      colors.push(nodeColor);
      outlineColors.push(outlineColor);
      outlineWidths.push(outlineWidth);

      // Sleek custom tooltip markup
      const exactFunding = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(funding);
      const tooltip = [
        `<b>${collab.legalName}</b>`,
        collab.businessName ? `Business Name: ${collab.businessName}` : '',
        `Type: ${collab.classificationType || 'N/A'}`,
        `Joint Funding: ${exactFunding}`,
        `Joint Projects: ${collab.numberOfProjects}`,
      ].filter(Boolean).join('<br>');

      hoverTexts.push(tooltip);
      customData.push({ type: 'partner', pic: collab.pic, index: partnerIndex });

      if (!isThisHovered) {
        annotations.push({
          x: coord.x,
          y: coord.y,
          text: `<b>${collab.legalName}</b>`,
          showarrow: false,
          yshift: -25,
          bgcolor: 'rgba(255, 255, 255, 0.92)',
          bordercolor: outlineColor,
          borderpad: 4,
          font: { size: 9, color: COLORS.TEXT_DARK }
        });
      }
    });

    traces.push({
      x: xCoordinates,
      y: yCoordinates,
      type: 'scatter',
      mode: 'markers',
      marker: {
        size: sizes,
        color: colors,
        line: {
          color: outlineColors,
          width: outlineWidths,
        },
      },
      hovertext: hoverTexts,
      hoverinfo: 'text', // Enable hover tooltip for full details
      customdata: customData,
      showlegend: false,
    });

    return { traces, annotations };
  }, [sortedCollaborations, nodeCoordinates, centerPartner, hoveredIndex]);

  // Handle clicking on partner nodes to open profile page
  const handleGraphClick = (event: any) => {
    if (event && event.points && event.points[0]) {
      const data = event.points[0].customdata;
      if (data && data.type === 'partner' && data.pic) {
        window.open(`/organization/${data.pic}`, '_blank');
      }
    }
  };

  // Track hover state to highlight/thicken the active connection line
  const handleGraphHover = (event: any) => {
    if (event && event.points && event.points[0]) {
      const data = event.points[0].customdata;
      if (data && data.index !== undefined && data.index !== 0) {
        setHoveredIndex(data.index);
      }
    }
  };

  const handleGraphUnhover = () => {
    setHoveredIndex(null);
  };

  if (!collaborations || collaborations.length === 0) {
    return null;
  }

  const graphTitle = sortedCollaborations.length === 30 ? `Collaboration Network (Top 30)` : `Collaboration Network (${sortedCollaborations.length})`;

  return (
    <div className="collaboration-network-graph-container">
      <div className="collaboration-graph-header">
        <div className="graph-title-block">
          <span className="graph-title">{graphTitle}</span>
        </div>
      </div>

      <div className="graph-visual-area">
        <Plot
          data={graphData.traces}
          layout={{
            annotations: graphData.annotations,
            showlegend: false,
            hovermode: 'closest',
            margin: { l: 15, r: 15, t: 15, b: 15 },
            xaxis: { 
              showgrid: false, 
              zeroline: false, 
              showticklabels: false, 
              range: [-16, 16],
              fixedrange: false,
              minallowed: -(maxRadius + 15),
              maxallowed: maxRadius + 15,
            },
            yaxis: { 
              showgrid: false, 
              zeroline: false, 
              showticklabels: false, 
              range: [-16, 16],
              fixedrange: false,
              scaleanchor: 'x',
              scaleratio: 1,
              minallowed: -(maxRadius + 15),
              maxallowed: maxRadius + 15,
            },
            dragmode: 'pan', // Default to pan to prevent accidental tap/click zoom-ins!
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
            autosize: true,
            uirevision: centerPartner.id, // Prevents zoom/pan from resetting when hovering over nodes
          }}
          config={{
            displayModeBar: true,
            displaylogo: false, // Remove Plotly watermark logo
            doubleClick: false, // Prevent sudden zoom-outs from double clicks/taps
            modeBarButtons: [['zoom2d', 'pan2d', 'zoomIn2d', 'zoomOut2d', 'resetScale2d']], // Add Box Zoom and Pan controls
            responsive: true,
            scrollZoom: true,
          }}
          style={{ width: '100%', height: graphHeight }}
          onClick={handleGraphClick}
          onHover={handleGraphHover}
          onUnhover={handleGraphUnhover}
          useResizeHandler={true}
        />
      </div>

      <div className="graph-legend">
        <div className="legend-section">
          <span className="legend-section-title">Funding in Joint Projects (Circle Size)</span>
          <div className="legend-items">
            <div className="legend-item">
              <span className="legend-circle small"></span>
              <span className="legend-text">&lt; 0.5 M€</span>
            </div>
            <div className="legend-item">
              <span className="legend-circle medium"></span>
              <span className="legend-text">0.5 M€ - 1.0 M€</span>
            </div>
            <div className="legend-item">
              <span className="legend-circle large"></span>
              <span className="legend-text">&gt; 1.0 M€</span>
            </div>
          </div>
        </div>
        <div className="legend-section border-left">
          <span className="legend-section-title">Actor Nodes</span>
          <div className="legend-items">
            <div className="legend-item">
              <span className="legend-circle center-actor"></span>
              <span className="legend-text">Active Organization</span>
            </div>
            <div className="legend-item">
              <span className="legend-circle partner-actor"></span>
              <span className="legend-text">Collaboration Partner</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
